import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  Switch,
  SvgIcon,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
// import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { DeleteOutline } from '@mui/icons-material';

import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';

import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';

import { GuardrailsService } from 'services/GuardrailsService';
import CreateCustomGuardrailModal from '../customPackage/CreateCustomGuardrailModal';
import { Toast } from 'shared/helpers';

interface GuardrailPolicy {
  _id: string;
  policyName: string;
  description: string;
  responseActions: string;
  enabled: boolean;
  type: string;
  monitoring: string;
  guardrailsGroupId: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  organizationId: string | null;
  isActive: boolean;
  __v: number;
}

interface PolicyGroup {
  _id: string;
  name: string;
  description: string | null;
  policies: GuardrailPolicy[];
}

interface SelectedPolicy {
  guardrailPolicyId: string;
  enabled: boolean;
}

interface PackagePayload {
  packageName: string;
  description: string;
  policies: SelectedPolicy[];
}

interface EditPackageData {
  _id: string;
  packageName: string;
  description: string;
  policies: SelectedPolicy[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  editData?: EditPackageData | null;
  onSubmit: (payload: PackagePayload) => void;
}

const CreateGuardrailPackageModal: React.FC<Props> = ({
  open,
  onClose,
  editData,
  onSubmit,
}) => {
  const [openCustomGuardrailModal, setOpenCustomGuardrailModal] =
    useState(false);
  const [editingPolicy, setEditingPolicy] = useState<GuardrailPolicy | null>(
    null
  );
  const [groups, setGroups] = useState<PolicyGroup[]>([]);
  const [packageName, setPackageName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPolicies, setSelectedPolicies] = useState<SelectedPolicy[]>(
    []
  );

  // LOAD POLICIES
  const loadGuardRailPolicy = (): void => {
    GuardrailsService.getAllGroupWithPolicies()
      .then((response) => {
        setGroups(response?.data?.data?.groups || []);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (open) {
      loadGuardRailPolicy();
    }
  }, [open]);

  // PREFILL EDIT DATA
  useEffect(() => {
    if (editData) {
      setPackageName(editData.packageName || '');
      setDescription(editData.description || '');
      setSelectedPolicies(editData.policies || []);
    } else {
      setPackageName('');
      setDescription('');
      setSelectedPolicies([]);
    }
  }, [editData, open]);

  // HANDLE POLICY TOGGLE
  const handleTogglePolicy = (policyId: string, checked: boolean): void => {
    if (checked) {
      setSelectedPolicies((prev) => [
        ...prev,
        {
          guardrailPolicyId: policyId,
          enabled: true,
        },
      ]);
    } else {
      setSelectedPolicies((prev) =>
        prev.filter((item) => item.guardrailPolicyId !== policyId)
      );
    }
  };

  // CREATE POLICY
  const createPolicy = (payload: any): void => {
    const finalPayload = {
      ...payload,
    };

    delete finalPayload.newGroupName;

    const apiCall = editingPolicy
      ? GuardrailsService.updatePolicy(editingPolicy._id, finalPayload)
      : GuardrailsService.createPolicy(finalPayload);

    apiCall
      .then(() => {
        Toast.showSuccessMessage(
          editingPolicy
            ? 'Policy updated successfully'
            : 'Policy created successfully'
        );

        loadGuardRailPolicy();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // HANDLE CREATE/UPDATE CUSTOM GUARDRAIL
  const handleCreateCustomGuardrail = (policyData: any): void => {
    // NEW GROUP FLOW
    if (policyData.guardrailsGroupId === 'new_group') {
      GuardrailsService.createGroup({
        name: policyData.newGroupName,
      })
        .then((res) => {
          createPolicy({
            ...policyData,
            guardrailsGroupId: res?.data?.data?._id,
          });
        })
        .catch((error) => {
          console.log(error);
        });

      return;
    }

    // NORMAL FLOW
    createPolicy(policyData);
  };

  // HANDLE EDIT POLICY
  const handleEditPolicy = (policy: GuardrailPolicy): void => {
    setEditingPolicy(policy);
    setOpenCustomGuardrailModal(true);
  };

  // SUBMIT
  const handleSubmit = (): void => {
    const payload = {
      packageName,
      description,
      policies: selectedPolicies,
    };

    onSubmit(payload);
  };

  // DELETE
  const handleDeleteClick = (policyId: string): void => {
    GuardrailsService.deletePolicy(policyId)
      .then((response) => {
        if (response.data.data.id)
          Toast.showSuccessMessage('Policy deleted successfully');
        loadGuardRailPolicy();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="guardrail-modal"
    >
      <Box className="modal-wrapper">
        {/* HEADER */}
        <Box className="modal-header">
          <Typography className="modal-title">
            {editData ? 'Edit Guardrail Package' : 'Create Guardrail Package'}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className="box-container">
          {/* PACKAGE NAME */}
          <Box className="form-group">
            <Typography className="field-label">Package Name</Typography>

            <TextField
              fullWidth
              placeholder="e.g. HR Agent Package"
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
            />
          </Box>

          {/* DESCRIPTION */}
          <Box className="form-group">
            <Typography className="field-label">Description</Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="What this package is designed for"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>

          {/* POLICY HEADER */}
          <Box className="policy-header">
            <Typography className="policy-title">
              Policies ({selectedPolicies.length} selected)
            </Typography>

            <Box className="policy-actions">
              {/* <AppPrimaryButtonOutlined
              title="Sensitivity Labels"
              className="btn-small"
              startIcon={<LocalOfferOutlinedIcon />}
              onClick={() => {}}
            /> */}

              <AppPrimaryButton
                title="Add Custom Guardrail"
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                }
                className="btn-small"
                onClick={() => {
                  setEditingPolicy(null);
                  setOpenCustomGuardrailModal(true);
                }}
              />
            </Box>
          </Box>

          {/* POLICY LIST */}
          <Box className="policy-container">
            {groups.map((group) => (
              <Box key={group._id} className="policy-group">
                <Typography className="policy-group-title">
                  {group.name}
                </Typography>

                {group.policies.map((item) => {
                  const isSelected = selectedPolicies.some(
                    (policy) => policy.guardrailPolicyId === item._id
                  );

                  return (
                    <Box key={item._id} className="policy-item">
                      <Switch
                        checked={isSelected}
                        onChange={(e) =>
                          handleTogglePolicy(item._id, e.target.checked)
                        }
                      />

                      <Box className="policy-content">
                        <Typography className="policy-name">
                          {item.policyName}
                        </Typography>

                        <Typography className="policy-description">
                          {item.description}
                        </Typography>

                        <Typography className="policy-action">
                          Actions: {item.responseActions}
                        </Typography>
                      </Box>

                      {/* Edit/Delete for custom policies */}
                      {item.type === 'custom' && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEditPolicy(item)}
                            className="edit-policy-btn"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(item._id)}
                          >
                            <DeleteOutline className="delete-icon" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>

        {/* FOOTER */}
        <Box className="modal-footer">
          <AppPrimaryButtonOutlined title="Cancel" onClick={onClose} />

          <AppPrimaryButton
            title={editData ? 'Update' : 'Create'}
            onClick={handleSubmit}
            disabled={!packageName.trim()}
          />
        </Box>
      </Box>

      {/* CUSTOM POLICY MODAL */}
      <CreateCustomGuardrailModal
        open={openCustomGuardrailModal}
        onClose={() => {
          setOpenCustomGuardrailModal(false);
          setEditingPolicy(null);
        }}
        onSubmit={handleCreateCustomGuardrail}
        editData={editingPolicy}
      />
    </Dialog>
  );
};

export default CreateGuardrailPackageModal;
